import { withScope, captureException as captureSentryException, captureMessage as captureSentryMessage, captureEvent, getClient, addIntegration, addBreadcrumb as addSentryBreadcrumb, } from '@sentry/browser';
import { hasValue } from '@famly/stat_ts-utils_has-value';
/**
 * Utility function used internally to check if Sentry object
 * can capture exceptions and messages properly.
 *
 * @property isSentryUsable
 * @type Ember.ComputedProperty
 */
export const isSentryUsable = () => {
    const isInitialized = Boolean(getClient());
    return isInitialized;
};
const isError = (element) => {
    return element instanceof Error;
};
/**
 * Sends an exception to Sentry if it's initialized.
 *
 * If Sentry hasn't been initialized, we log the error to console.
 *
 * @param error The error to send to Sentry
 * @param data Optional data object
 */
export const captureException = (error, data) => {
    if (isSentryUsable()) {
        const exception = isError(error) ? error : JSON.stringify(error);
        withScope(scope => {
            if (data && data.extra) {
                const extra = data.extra;
                Object.keys(extra).map(extraKey => {
                    scope.setExtra(extraKey, extra[extraKey]);
                });
            }
            if (data === null || data === void 0 ? void 0 : data.tags) {
                const tags = data.tags;
                Object.keys(tags).map(tagKey => {
                    scope.setTag(tagKey, tags[tagKey]);
                });
            }
            scope.setLevel(data && data.level ? data.level : 'error');
            captureSentryException(exception);
        });
    }
    else {
        console.error('Sentry is not initialized', error);
    }
};
/**
 * Tries to have Sentry capture message, or send it to console.
 *
 * @method captureMessage
 * @param  {String} message The message to capture
 * @param  {Object} data Optional data object
 * @return {Boolean}
 */
export const captureMessage = (message, data) => {
    if (isSentryUsable()) {
        withScope(scope => {
            if (data && data.extra) {
                const extra = data.extra;
                Object.keys(extra).map(extraKey => {
                    scope.setExtra(extraKey, extra[extraKey]);
                });
            }
            if (data === null || data === void 0 ? void 0 : data.tags) {
                const tags = data.tags;
                Object.keys(tags).map(tagKey => {
                    scope.setTag(tagKey, tags[tagKey]);
                });
            }
            if (data === null || data === void 0 ? void 0 : data.fingerprint) {
                scope.setFingerprint(data.fingerprint);
            }
            scope.setLevel(data && data.level ? data.level : 'error');
            captureSentryMessage(message);
        });
        return true;
    }
    return false;
};
/**
 * Sends an event to Sentry.
 *
 * Note: This doesn't capture a stack trace.
 *
 * @param message The message of the event
 * @param data Optional data object
 */
export const sendEvent = (message, data) => {
    if (!isSentryUsable()) {
        return;
    }
    captureEvent({
        message,
        extra: data === null || data === void 0 ? void 0 : data.extra,
        fingerprint: data === null || data === void 0 ? void 0 : data.fingerprint,
        level: data === null || data === void 0 ? void 0 : data.level,
        tags: data === null || data === void 0 ? void 0 : data.tags,
    });
};
/**
 * Does what Sentry's `getReplay` does. Importing that function would pull all of Session Replay,
 * which shares its module, into every bundle that imports this one.
 */
const getReplay = () => { var _a; return (_a = getClient()) === null || _a === void 0 ? void 0 : _a.getIntegrationByName('Replay'); };
/** Settles once Replay is added or has failed to load; undefined where it is never added. */
let replayAdded;
/**
 * Adds Session Replay once `load` resolves, so its code can stay out of the startup bundle.
 * Recordings started or stopped before then take effect once it is added.
 */
export const addReplayIntegration = (load) => {
    replayAdded = load().then(addIntegration, () => {
        // Replay is best effort: a page that cannot load it records nothing instead of failing.
    });
};
const startReplay = (replay) => {
    // start can throw if there is an ongoing recording already.
    try {
        replay.start();
    }
    catch (_a) { }
    // Sometimes `start` doesn't really start. It seems to help to flush some of the recording.
    replay.flush({ continueRecording: true });
};
/** Starts a manual recording. Returns true if recording started, or will once Replay is added. */
export const startRecording = () => {
    const replay = getReplay();
    if (hasValue(replay)) {
        startReplay(replay);
        return true;
    }
    if (!replayAdded) {
        return false;
    }
    replayAdded.then(() => {
        const addedReplay = getReplay();
        if (hasValue(addedReplay)) {
            startReplay(addedReplay);
        }
    });
    return true;
};
/** Stops a recording. Returns true if recording stopped. */
export const stopRecording = () => (replayAdded !== null && replayAdded !== void 0 ? replayAdded : Promise.resolve()).then(() => {
    const replay = getReplay();
    if (!hasValue(replay)) {
        return false;
    }
    return replay.flush().then(() => {
        const flushedReplay = getReplay();
        if (!hasValue(flushedReplay)) {
            return false;
        }
        flushedReplay.stop();
        return true;
    });
});
export const addBreadcrumb = (breadcrumb) => {
    if (isSentryUsable()) {
        addSentryBreadcrumb(breadcrumb);
    }
    else {
        console.warn('Sentry is not initialized, cannot add breadcrumb', breadcrumb);
    }
};
